import {
    CapabilityAccessStatus,
    type ComplianceRequirementView
} from "@solomon-apps/shared";
import { StatusPill } from "@solomon-apps/shared/src/components/Metrics/StatusPill";
import { Badge } from "@solomon-apps/shared/src/components/ui/badge";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Card } from "@solomon-apps/shared/src/components/ui/card";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useClientCapabilityAccess } from "@/hooks/useClientCapabilityAccess";
import { useClientComplianceRequirements } from "@/hooks/useClientComplianceRequirements";
import { ClientPage } from "@/pages/shared/ClientPage";
import { fetchClientAgreementDocument } from "@/services/clientCapabilityAccess";

import {
    complianceRequirementStatusCopy,
    countRequirementsByStage,
    hasComplianceRequirements,
    sortComplianceRequirements
} from "./compliancePageModel";

export function CompliancePage(): React.ReactElement {
    const { requirements, isLoading, error, hasSelectedContext } =
        useClientComplianceRequirements();
    const capabilityAccess = useClientCapabilityAccess();
    const selectedRelationshipRef = useRef(capabilityAccess.relationshipUuid);
    selectedRelationshipRef.current = capabilityAccess.relationshipUuid;
    const [agreementDocumentError, setAgreementDocumentError] = useState<{
        relationshipUuid: string;
        message: string;
    } | null>(null);
    const downloadAgreementDocument = async (
        kind: "signed-document" | "completion-certificate"
    ): Promise<void> => {
        const requirementUuid =
            capabilityAccess.access?.agreement.requirementUuid;
        const relationshipUuid = capabilityAccess.relationshipUuid;
        if (!requirementUuid || !relationshipUuid) return;
        setAgreementDocumentError(null);
        const previewWindow = window.open("", "_blank");
        if (!previewWindow) {
            setAgreementDocumentError({
                relationshipUuid,
                message:
                    "Allow pop-ups for this site, then try downloading the agreement again."
            });
            return;
        }
        previewWindow.opener = null;
        try {
            const result = await fetchClientAgreementDocument({
                requirementUuid,
                kind
            });
            if (selectedRelationshipRef.current !== relationshipUuid) {
                previewWindow.close();
                return;
            }
            const url = URL.createObjectURL(result.blob);
            previewWindow.location.href = url;
            window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (downloadError) {
            previewWindow.close();
            if (selectedRelationshipRef.current !== relationshipUuid) return;
            setAgreementDocumentError({
                relationshipUuid,
                message:
                    downloadError instanceof Error
                        ? downloadError.message
                        : "Agreement document download failed. Try again."
            });
        }
    };
    const agreementFeedback =
        agreementDocumentError?.relationshipUuid ===
        capabilityAccess.relationshipUuid ? (
            <Text
                variant="small"
                render={<p role="alert" />}
                className="px-6 py-3 text-error-base"
            >
                {agreementDocumentError.message}
            </Text>
        ) : null;
    const sortedRequirements = useMemo(
        () => sortComplianceRequirements(requirements?.requirements ?? []),
        [requirements]
    );
    const stageCounts = useMemo(
        () => countRequirementsByStage(sortedRequirements),
        [sortedRequirements]
    );

    if (!hasSelectedContext) {
        return (
            <ClientPage title="Compliance">
                <ComplianceMessage
                    title="Select a distributor relationship"
                    body="Choose a distributor in the sidebar before reviewing compliance requirements."
                />
            </ClientPage>
        );
    }

    if (isLoading) {
        return (
            <ClientPage title="Compliance">
                <ComplianceMessage title="Loading compliance requirements." />
            </ClientPage>
        );
    }

    if (error) {
        return (
            <ClientPage title="Compliance">
                <CapabilityAccessStatus
                    access={capabilityAccess.access}
                    error={capabilityAccess.error}
                    isLoading={capabilityAccess.isLoading}
                    audience="client"
                    onDocument={(kind) => void downloadAgreementDocument(kind)}
                />
                {agreementFeedback}
                <ComplianceMessage
                    title="Compliance requirements could not be loaded."
                    body="Refresh the page or try again after the compliance API is available."
                />
            </ClientPage>
        );
    }

    if (!requirements || !hasComplianceRequirements(requirements)) {
        return (
            <ClientPage title="Compliance">
                <CapabilityAccessStatus
                    access={capabilityAccess.access}
                    error={capabilityAccess.error}
                    isLoading={capabilityAccess.isLoading}
                    audience="client"
                    onDocument={(kind) => void downloadAgreementDocument(kind)}
                />
                {agreementFeedback}
                <ComplianceMessage
                    title="No active compliance requirements."
                    body="Your distributor has not assigned any compliance requirements for this relationship."
                />
            </ClientPage>
        );
    }
    const requirementList = requirements;

    return (
        <ClientPage title="Compliance">
            <CapabilityAccessStatus
                access={capabilityAccess.access}
                error={capabilityAccess.error}
                isLoading={capabilityAccess.isLoading}
                audience="client"
                onDocument={(kind) => void downloadAgreementDocument(kind)}
            />
            {agreementFeedback}
            <section className="border-b border-line-base">
                <div className="grid grid-cols-2 divide-x divide-y divide-line-base sm:grid-cols-4 sm:divide-y-0">
                    <StatCell
                        label="Requirements"
                        value={requirementList.summary.requirementCount}
                    />
                    <StatCell
                        label="Approved"
                        value={requirementList.summary.satisfiedCount}
                    />
                    <StatCell
                        label="Pending review"
                        value={
                            stageCounts.pending_review +
                            stageCounts.pending_update
                        }
                    />
                    <StatCell
                        label="Action needed"
                        value={stageCounts.missing + stageCounts.rejected}
                    />
                </div>
            </section>

            {requirementList.summary.blocksClientWrites ? (
                <section className="border-b border-line-base bg-error-inverse px-6 py-4">
                    <Text variant="small" className="text-foreground-base">
                        Hard compliance requirements are incomplete. Company
                        recipient write actions stay blocked until the required
                        evidence is approved.
                    </Text>
                </section>
            ) : null}

            <section className="grid gap-4 px-6 py-6 xl:grid-cols-[minmax(0,1fr)_320px]">
                <div className="space-y-3">
                    {sortedRequirements.map((requirement) => (
                        <RequirementCard
                            key={requirement.uuid}
                            requirement={requirement}
                        />
                    ))}
                </div>
                <aside className="border border-line-base bg-surface-ground px-4 py-4">
                    <Text variant="h3" className="text-foreground-base">
                        Review queue
                    </Text>
                    <div className="mt-4 space-y-3">
                        <QueueLine
                            label="Missing"
                            value={stageCounts.missing}
                        />
                        <QueueLine
                            label="Pending distributor review"
                            value={stageCounts.pending_review}
                        />
                        <QueueLine
                            label="Updates pending"
                            value={stageCounts.pending_update}
                        />
                        <QueueLine
                            label="Approved"
                            value={stageCounts.approved}
                        />
                    </div>
                </aside>
            </section>
        </ClientPage>
    );
}

interface RequirementCardProps {
    requirement: ComplianceRequirementView;
}

function RequirementCard({
    requirement
}: RequirementCardProps): React.ReactElement {
    const status = complianceRequirementStatusCopy(requirement);
    const latestVersion = requirement.approvedEvidenceVersion;
    const documentCount = latestVersion?.documents.length ?? 0;
    const canOpenOnboarding =
        status.stage === "missing" || status.stage === "rejected";

    return (
        <Card
            asElement="article"
            surface="ground"
            topStripe={
                requirement.blocksClientWrites
                    ? { tone: "error" }
                    : requirement.warningOnly
                      ? { tone: "warning" }
                      : undefined
            }
            className="px-4 py-4"
        >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <Text
                            variant="h3"
                            render={<h2 />}
                            className="text-foreground-base"
                        >
                            {requirement.module.displayName}
                        </Text>
                        <Badge
                            variant={
                                requirement.gate === "hard"
                                    ? "error"
                                    : "warning"
                            }
                        >
                            {requirement.gate === "hard" ? "Hard" : "Soft"}
                        </Badge>
                    </div>
                    <Text
                        variant="small"
                        className="mt-2 max-w-3xl text-foreground-muted"
                    >
                        {requirement.module.description}
                    </Text>
                    <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
                        <StatusPill
                            indicatorState={status.indicator}
                            textVariant="small"
                            textClassName={status.textClassName}
                        >
                            {status.label}
                        </StatusPill>
                        {latestVersion ? (
                            <MetaText
                                label={`Version ${latestVersion.versionNumber}`}
                                value={formatDate(latestVersion.submittedAt)}
                            />
                        ) : null}
                        {documentCount > 0 ? (
                            <MetaText
                                label="Documents"
                                value={documentCount.toLocaleString("en-US")}
                            />
                        ) : null}
                    </div>
                    <Text
                        variant="fine-print"
                        className="mt-3 max-w-2xl text-foreground-muted"
                    >
                        {status.description}
                    </Text>
                </div>

                {canOpenOnboarding ? (
                    <Button
                        render={<Link to="/onboarding" />}
                        variant="raised"
                        size="sm"
                        className="shrink-0"
                    >
                        Open onboarding
                    </Button>
                ) : null}
            </div>
        </Card>
    );
}

interface CountDisplayProps {
    label: string;
    value: number;
}

function StatCell({ label, value }: CountDisplayProps): React.ReactElement {
    return (
        <div className="px-6 py-5">
            <Text
                variant="fine-print"
                render={<span />}
                className="block text-foreground-muted"
            >
                {label}
            </Text>
            <Text
                variant="number-lg"
                render={<span />}
                className="mt-2 block text-foreground-base"
            >
                {value.toLocaleString("en-US")}
            </Text>
        </div>
    );
}

function QueueLine({ label, value }: CountDisplayProps): React.ReactElement {
    return (
        <div className="flex items-baseline justify-between gap-4 border-b border-line-base pb-2 last:border-b-0 last:pb-0">
            <Text variant="small" className="text-foreground-muted">
                {label}
            </Text>
            <Text
                variant="number"
                render={<span />}
                className="text-foreground-base"
            >
                {value.toLocaleString("en-US")}
            </Text>
        </div>
    );
}

interface MetaTextProps {
    label: string;
    value: string | null;
}

function MetaText({ label, value }: MetaTextProps): React.ReactElement {
    return (
        <Text
            variant="fine-print"
            render={<span />}
            className="text-foreground-muted"
        >
            {label}
            {value ? `: ${value}` : null}
        </Text>
    );
}

interface ComplianceMessageProps {
    title: string;
    body?: string;
}

function ComplianceMessage({
    title,
    body
}: ComplianceMessageProps): React.ReactElement {
    return (
        <section className="max-w-2xl px-6 py-10">
            <Text variant="h2" className="text-foreground-base">
                {title}
            </Text>
            {body ? (
                <Text variant="body" className="mt-2 text-foreground-muted">
                    {body}
                </Text>
            ) : null}
        </section>
    );
}

function formatDate(value: string | null): string | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
    }).format(date);
}
